import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Megaphone, AlertOctagon, Wrench,
  FileText, Send, Download, Plus, Trash2, RefreshCw, Mail,
  BookOpen, FileUp, History as HistoryIcon,
} from "lucide-react";
import PartnerContentPanel from "./admin/AdminTrustCenter.PartnerContent";
import "./DeveloperPortal.css";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminTrustCenter() {
  const [tab, setTab] = useState("announcements");
  return (
    <div className="hw-partners" data-testid="admin-trust-center">
      <div className="hw-partners__header">
        <div>
          <h2><ShieldCheck className="w-5 h-5" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
            Trust Center & Governance Digest</h2>
          <p className="hw-partners__sub">
            Manage public-facing announcements, incidents, and planned
            maintenance shown on the <a href="/trust-center" target="_blank" rel="noreferrer">Trust Center</a>.
            Generate + email the weekly Executive Governance Digest.
          </p>
        </div>
      </div>
      <div className="hw-dev__tabs" style={{ borderBottomColor: "#E5E7EB" }}>
        {[
          ["announcements", "Announcements", Megaphone],
          ["incidents", "Incidents", AlertOctagon],
          ["maintenance", "Maintenance", Wrench],
          ["partner-content", "Partner Content", BookOpen],
          ["digest", "Governance Digest", FileText],
        ].map(([id, label, Icon]) => (
          <button key={id} className={`hw-dev__tab ${tab === id ? "hw-dev__tab--active" : ""}`}
                  style={{ color: tab === id ? "#0F172A" : "#64748B" }}
                  onClick={() => setTab(id)} data-testid={`atc-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.25rem" }} />
            {label}
          </button>
        ))}
      </div>

      {tab === "announcements" && <AnnouncementsPanel />}
      {tab === "incidents" && <IncidentsPanel />}
      {tab === "maintenance" && <MaintenancePanel />}
      {tab === "partner-content" && <PartnerContentPanel />}
      {tab === "digest" && <DigestPanel />}
    </div>
  );
}

function AnnouncementsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", severity: "info", published: true });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/trust-center/announcements");
      setRows(data.announcements || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/trust-center/announcements", form);
      toast.success("Announcement published");
      setForm({ title: "", body: "", severity: "info", published: true });
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/trust-center/announcements/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="hw-partners__section" data-testid="atc-announcements-panel">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Announcements</h3>
        <button className="hw-partners__btn hw-partners__btn--primary" onClick={() => setShowForm(!showForm)}
                data-testid="atc-new-announcement">
          <Plus className="w-3 h-3" /> New
        </button>
      </div>
      {showForm && (
        <div className="hw-partners__form-grid" style={{ marginTop: "0.6rem" }}>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Title
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                    data-testid="atc-ann-title" /></label>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Body
            <textarea rows={3} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
                        data-testid="atc-ann-body" /></label>
          <label className="hw-partners__label">Severity
            <select value={form.severity} onChange={(e) => setForm({...form, severity: e.target.value})}>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select></label>
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: "0.4rem" }}>
            <button className="hw-partners__btn" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="hw-partners__btn hw-partners__btn--primary" onClick={create}
                    disabled={!form.title || !form.body} data-testid="atc-ann-submit">Publish</button>
          </div>
        </div>
      )}
      <table className="hw-partners__table" style={{ marginTop: "0.6rem" }}>
        <thead><tr><th>Title</th><th>Severity</th><th>Published</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={4} style={{ textAlign: "center", padding: "1rem", color: "#64748B" }}>None yet.</td></tr>
          ) : rows.map((a) => (
            <tr key={a.id}>
              <td><strong>{a.title}</strong><div style={{ fontSize: "0.7rem", color: "#64748B" }}>{a.body.slice(0, 80)}…</div></td>
              <td>{a.severity}</td>
              <td>{a.published ? "yes" : "no"}</td>
              <td><button className="hw-partners__btn hw-partners__btn--danger" onClick={() => del(a.id)}>
                <Trash2 className="w-3 h-3" />
              </button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function IncidentsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", summary: "", impact: "minor", status: "investigating" });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/trust-center/incidents?limit=100`);
      setRows(data.incidents || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/trust-center/incidents", form);
      toast.success("Incident opened"); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resolve = async (id) => {
    try {
      await api.patch(`/admin/trust-center/incidents/${id}`, { status: "resolved" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this incident?")) return;
    try { await api.delete(`/admin/trust-center/incidents/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="hw-partners__section" data-testid="atc-incidents-panel">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Incidents</h3>
        <button className="hw-partners__btn hw-partners__btn--primary" onClick={() => setShowForm(!showForm)}
                data-testid="atc-new-incident">
          <Plus className="w-3 h-3" /> Open incident
        </button>
      </div>
      {showForm && (
        <div className="hw-partners__form-grid" style={{ marginTop: "0.6rem" }}>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Title
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                    data-testid="atc-inc-title" /></label>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Summary
            <textarea rows={2} value={form.summary}
                        data-testid="atc-inc-summary"
                        onChange={(e) => setForm({...form, summary: e.target.value})} /></label>
          <label className="hw-partners__label">Impact
            <select value={form.impact} onChange={(e) => setForm({...form, impact: e.target.value})}>
              <option>minor</option><option>major</option><option>outage</option><option>degraded</option>
            </select></label>
          <div style={{ gridColumn: "1 / -1" }}>
            <button className="hw-partners__btn" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="hw-partners__btn hw-partners__btn--primary" onClick={create}
                    disabled={!form.title || (form.summary || "").trim().length < 3}
                    data-testid="atc-inc-submit">Open</button>
          </div>
        </div>
      )}
      <table className="hw-partners__table" style={{ marginTop: "0.6rem" }}>
        <thead><tr><th>Title</th><th>Impact</th><th>Status</th><th>Started</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "#64748B" }}>No incidents.</td></tr>
          ) : rows.map((i) => (
            <tr key={i.id}>
              <td>{i.title}</td><td>{i.impact}</td><td>{i.status}</td>
              <td style={{ fontSize: "0.75rem", color: "#64748B" }}>{i.started_at?.slice(0, 10)}</td>
              <td>{i.status !== "resolved" &&
                <button className="hw-partners__btn" onClick={() => resolve(i.id)}>Resolve</button>}
                <button className="hw-partners__btn hw-partners__btn--danger"
                          data-testid={`atc-inc-delete-${i.id}`}
                          onClick={() => del(i.id)} style={{ marginLeft: "0.35rem" }}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function MaintenancePanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", window_start: "", window_end: "" });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/trust-center/maintenance?future_only=false&limit=100`);
      setRows(data.maintenance || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/trust-center/maintenance", form);
      toast.success("Maintenance scheduled"); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this maintenance window?")) return;
    try { await api.delete(`/admin/trust-center/maintenance/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="hw-partners__section" data-testid="atc-maintenance-panel">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Planned Maintenance</h3>
        <button className="hw-partners__btn hw-partners__btn--primary" onClick={() => setShowForm(!showForm)}
                data-testid="atc-new-maintenance">
          <Plus className="w-3 h-3" /> Schedule
        </button>
      </div>
      {showForm && (
        <div className="hw-partners__form-grid" style={{ marginTop: "0.6rem" }}>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Title
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                    data-testid="atc-mnt-title" /></label>
          <label className="hw-partners__label" style={{ gridColumn: "1 / -1" }}>Description
            <textarea rows={2} value={form.description}
                        data-testid="atc-mnt-description"
                        onChange={(e) => setForm({...form, description: e.target.value})} /></label>
          <label className="hw-partners__label">Window start (ISO)
            <input placeholder="2026-07-10T02:00:00Z" value={form.window_start}
                    data-testid="atc-mnt-window-start"
                    onChange={(e) => setForm({...form, window_start: e.target.value})} /></label>
          <label className="hw-partners__label">Window end (ISO)
            <input placeholder="2026-07-10T04:00:00Z" value={form.window_end}
                    data-testid="atc-mnt-window-end"
                    onChange={(e) => setForm({...form, window_end: e.target.value})} /></label>
          <div style={{ gridColumn: "1 / -1" }}>
            <button className="hw-partners__btn" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="hw-partners__btn hw-partners__btn--primary" onClick={create}
                    disabled={!form.title || !form.window_start || !form.window_end
                                || (form.description || "").trim().length < 3}
                    data-testid="atc-mnt-submit">Schedule</button>
          </div>
        </div>
      )}
      <table className="hw-partners__table" style={{ marginTop: "0.6rem" }}>
        <thead><tr><th>Title</th><th>Window</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} style={{ textAlign: "center", padding: "1rem", color: "#64748B" }}>None.</td></tr>
          ) : rows.map((m) => (
            <tr key={m.id}><td>{m.title}</td>
              <td style={{ fontSize: "0.75rem", color: "#64748B" }}>{m.window_start} → {m.window_end}</td>
              <td><button className="hw-partners__btn hw-partners__btn--danger"
                              data-testid={`atc-mnt-delete-${m.id}`}
                              onClick={() => del(m.id)}>
                <Trash2 className="w-3 h-3" />
              </button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function DigestPanel() {
  const [recipients, setRecipients] = useState([]);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", name: "", tier: "executive" });
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, l, h, p] = await Promise.all([
        api.get("/admin/governance-digest/recipients"),
        api.get("/admin/governance-digest/latest"),
        api.get("/admin/governance-digest/history"),
        api.get("/admin/governance-digest/preview"),
      ]);
      setRecipients(r.data.recipients || []);
      setLatest(l.data.digest);
      setHistory(h.data.digests || []);
      setPreview(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRec = async () => {
    try {
      await api.post("/admin/governance-digest/recipients", addForm);
      toast.success("Recipient added");
      setAddForm({ email: "", name: "", tier: "executive" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeRec = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try { await api.delete(`/admin/governance-digest/recipients/${encodeURIComponent(email)}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const generate = async (test) => {
    setBusy(true);
    try {
      const body = test && testEmail ? { test_recipient: testEmail } : {};
      const { data } = await api.post("/admin/governance-digest/generate", body);
      toast.success(`Digest generated · sent to ${data.delivery?.sent || 0} recipient(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const downloadPdf = (id) => {
    window.open(`${BASE}/api/admin/governance-digest/${id}/pdf`, "_blank");
  };

  return (
    <>
      <section className="hw-partners__section" data-testid="atc-digest-panel">
        <h3 style={{ margin: 0 }}>Executive Governance Digest</h3>
        <p style={{ fontSize: "0.85rem", color: "#475569", marginTop: "0.25rem" }}>
          Weekly Monday 07:00 America/New_York. Generates a branded PDF and
          emails it via Resend to the recipients below.
        </p>

        <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
          <button className="hw-partners__btn hw-partners__btn--primary" onClick={() => generate(false)}
                  disabled={busy} data-testid="digest-generate-now">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Generate & send now
          </button>
          <input placeholder="test@you.com" value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="digest-test-email"
                  style={{ padding: "0.42rem 0.55rem", border: "1px solid #CBD5E1",
                            borderRadius: "0.4rem", fontSize: "0.85rem" }} />
          <button className="hw-partners__btn" onClick={() => generate(true)}
                  disabled={busy || !testEmail} data-testid="digest-send-test">
            <Mail className="w-3.5 h-3.5" /> Send test
          </button>
          <button className="hw-partners__btn" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {latest && (
          <div style={{ marginTop: "0.85rem", padding: "0.6rem 0.85rem", background: "#F8FAFC",
                          border: "1px solid #E5E7EB", borderRadius: "0.5rem", fontSize: "0.85rem" }}>
            <strong>Latest digest:</strong> {latest.week_of} · generated {latest.generated_at?.slice(0, 19)}Z ·
            {" "}sent to {latest.delivery?.sent ?? 0} · PDF {Math.round((latest.pdf_bytes_len || 0) / 1024)} KB
            {" "}<button className="hw-partners__btn" onClick={() => downloadPdf(latest.digest_id)}
                            data-testid="digest-download-latest"
                            style={{ padding: "0.15rem 0.5rem", marginLeft: "0.4rem" }}>
              <Download className="w-3 h-3" /> Download
            </button>
          </div>
        )}
      </section>

      <section className="hw-partners__section" data-testid="atc-digest-recipients">
        <h3 style={{ margin: 0 }}>Recipients</h3>
        <div className="hw-partners__form-grid" style={{ marginTop: "0.5rem" }}>
          <label className="hw-partners__label">Email
            <input value={addForm.email} onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                    data-testid="digest-add-email" /></label>
          <label className="hw-partners__label">Name
            <input value={addForm.name} onChange={(e) => setAddForm({...addForm, name: e.target.value})} /></label>
          <label className="hw-partners__label">Tier
            <select value={addForm.tier} onChange={(e) => setAddForm({...addForm, tier: e.target.value})}>
              <option value="executive">Executive Leadership</option>
              <option value="compliance">Compliance Officer</option>
              <option value="board">Board Member</option>
            </select></label>
          <div><button className="hw-partners__btn hw-partners__btn--primary" onClick={addRec}
                          disabled={!addForm.email} data-testid="digest-add-submit"
                          style={{ marginTop: "1rem" }}>Add recipient</button></div>
        </div>

        <table className="hw-partners__table" style={{ marginTop: "0.5rem" }}>
          <thead><tr><th>Email</th><th>Name</th><th>Tier</th><th></th></tr></thead>
          <tbody>
            {recipients.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: "center", padding: "1rem", color: "#64748B" }}>
                No recipients — the weekly job will still archive the PDF but nothing gets emailed.
              </td></tr>
            ) : recipients.map((r) => (
              <tr key={r.email}>
                <td>{r.email}</td><td>{r.name || "—"}</td><td>{r.tier}</td>
                <td><button className="hw-partners__btn hw-partners__btn--danger"
                              onClick={() => removeRec(r.email)}>
                  <Trash2 className="w-3 h-3" />
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {preview && (
        <section className="hw-partners__section">
          <h3 style={{ margin: "0 0 0.35rem" }}>Data preview (this week&apos;s digest contents)</h3>
          <div style={{ fontSize: "0.78rem", color: "#475569" }}>
            Status: <strong>{preview.status?.overall_status}</strong> · Uptime 30d:
            <strong> {preview.status?.uptime_30d_pct}%</strong> · Partners:
            <strong> {preview.partners?.total}</strong> · Open exceptions:
            <strong> {preview.governance?.open_exceptions}</strong> · Referrals this week:
            <strong> {preview.impact?.referrals_7d}</strong>
          </div>
        </section>
      )}

      {history.length > 1 && (
        <section className="hw-partners__section" data-testid="atc-digest-history">
          <h3 style={{ margin: 0 }}>Archive</h3>
          <table className="hw-partners__table" style={{ marginTop: "0.5rem" }}>
            <thead><tr><th>Week</th><th>Generated</th><th>Sent</th><th></th></tr></thead>
            <tbody>
              {history.map((d) => (
                <tr key={d.digest_id}>
                  <td>{d.week_of}</td>
                  <td style={{ fontSize: "0.75rem", color: "#64748B" }}>{d.generated_at?.slice(0, 19)}Z</td>
                  <td>{d.delivery?.sent ?? 0}</td>
                  <td><button className="hw-partners__btn" onClick={() => downloadPdf(d.digest_id)}>
                    <Download className="w-3 h-3" /> PDF
                  </button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
